import React, { useState, useEffect, useCallback, useMemo } from "react";
import { connect } from "react-redux";
import moment from "moment-timezone";
import { resolveFinding } from "redux/actions/Findings";
import { loadPageData, exportRequestAction } from "redux/actions/Request";
import { findingColumnOptions } from "lib/util/FindingUtils";
import SimpleTable from "views/Components/SimpleTable";

import FindingsDetailView from "./FindingsDetailView";

import FilterEditor from "views/Components/Reporting/FilterEditor";
import { TimeControls } from "views/Components/TimespanPicker";
import Time from "lib/time";

import { get } from "lodash";

import {
  useQueryControls,
  useResultControls,
  SaveRequestModal,
} from "views/Components/QueryControls";

import {
  Chip,
  Fade,
  Button,
  Switch,
  Dialog,
  Select,
  MenuItem,
  TextField,
  InputLabel,
  IconButton,
  FormControl,
  InputAdornment,
} from "@material-ui/core";

import { makeStyles } from "@material-ui/core/styles";

import CloseIcon from "@material-ui/icons/Close";
import SearchIcon from "@material-ui/icons/Search";
import PublicIcon from "@material-ui/icons/Public";
import DeleteIcon from "@material-ui/icons/DeleteOutline";

import { copyTextToClipboard, getValue } from "utils";

import FindingsResolutionDialog from "views/Components/Finding/FindingsResolutionDialog";

const useStyles = makeStyles((theme) => ({
  flexContainer: {
    display: "flex",
  },
  scheduledReportsButton: {
    height: 43,
    borderRadius: 10,
    borderColor: theme.palette.divider,
    backgroundColor: theme.palette.background.paper,
    "& a": {
      margin: 0,
      flexWrap: "no-wrap",
      textTransform: "none",
      textDecoration: "none",
    },
  },
  presetButtonContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
  },
  labelText: {
    fontSize: 14,
    marginBottom: 5,
  },
  filterContainer: {
    padding: 20,
    borderRadius: 5,
    display: "flex",
    marginBottom: 20,
    flexDirection: "column",
    justifyContent: "space-between",
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
  },
  filterSectionOuterContainer: {
    width: "100%",
    justifyContent: "flex-start",
  },
  advancedSectionContainer: {
    paddingBottom: 20,
  },
  distinctCountContainer: {
    display: "flex",
    padding: "20px 0",
    alignItems: "center",
  },
  noColumnText: {
    margin: 0,
    padding: 10,
    fontSize: 14,
  },
  filterSelect: {
    height: 40,
    padding: 5,
    width: "100%",
    display: "flex",
    borderRadius: 5,
    cursor: "pointer",
    flexWrap: "nowrap",
    alignItems: "center",
    justifyContent: "flex-start",
    border: `1px solid ${theme.palette.divider}`,
    "& p": {
      margin: "0 0 0 5px",
      color: theme.palette.text.secondary,
    },
  },
  emptyFilterContainer: {
    padding: 15,
    fontSize: 15,
    color: "white",
    borderRadius: 10,
    margin: "15px 0",
    backgroundColor: theme.palette.success.main,
    "& p": {
      margin: 0,
    },
  },
  titleText: {
    margin: 0,
    fontWeight: "bold",
  },
  sectionText: {
    fontSize: 15,
    marginBottom: 5,
    marginRight: 10,
    color: theme.palette.text.primary,
  },
  chipContainer: {
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "flex-start",
  },
  chip: {
    margin: 5,
  },
  buttonContainer: {
    paddingTop: 20,
    display: "flex",
    borderTop: `1px solid ${theme.palette.divider}`,
  },
  button: {
    width: 100,
    height: 50,
    margin: "10px 0",
    padding: "0 50px",
    boxShadow: "none",
    textTransform: "none",
  },
  select: {
    width: 200,
    borderRadius: 20,
    backgroundColor: theme.palette.background.paper,
  },
  row: {
    width: "100%",
    display: "flex",
    margin: "10px 0",
    alignItems: "center",
  },
  inputContainer: {
    display: "flex",
    marginRight: 10,
    flexDirection: "column",
    alignItems: "flex-start",
  },
  textContainer: {
    minWidth: 250,
    display: "flex",
    borderRadius: 10,
    cursor: "pointer",
    alignItems: "center",
    padding: "10px 15px",
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
    "& p": {
      fontSize: 15,
      marginBottom: 0,
    },
  },
  presetDialog: {
    width: 800,
    height: 600,
    paddingHorizontal: 20,
  },
  titleOuterContainer: {
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  titleContainer: {
    padding: 20,
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
  },
  queryContainer: {
    width: "100%",
    display: "flex",
    padding: "0 20px",
    overflowY: "scroll",
    flexDirection: "column",
    alignItems: "flex-start",
  },
  sectionTitleText: {
    margin: 0,
    fontSize: 13,
    padding: "10px 0",
    fontWeight: "bold",
  },
  queryNameContainer: {
    padding: 10,
    width: "100%",
    display: "flex",
    cursor: "pointer",
    alignItems: "center",
    justifyContent: "space-between",
    borderBottom: `1px solid ${theme.palette.divider}`,
    "& p": {
      margin: 0,
    },
  },
  confirmContainer: {
    display: "flex",
    alignItems: "center",
    "& p": {
      color: theme.palette.text.secondary,
    },
  },
  confirmDelete: {
    color: theme.palette.error.main,
  },
  noAssignees: {
    fontStyle: "italic",
  },
  notesDialogContainer: {
    padding: 20,
    width: 400,
  },
  resolutionTextField: {
    width: "100%",
  },
}));

const usePriorityStyles = makeStyles((theme) => ({
  container: {
    display: "flex",
    alignItems: "center",
  },
  circle: {
    width: 15,
    height: 15,
    marginRight: 5,
    borderRadius: "50%",
  },
}));

// TODO: this should be in our constants repo soon
const resolutionOptions = [
  { value: 10, label: "Valid" },
  { value: 20, label: "False Positive" },
  { value: 30, label: "No Action Needed" },
  { value: 40, label: "Risk Accepted" },
];

const Priority = (props) => {
  const classes = usePriorityStyles();
  return (
    <span className={classes.container}>
      <div
        className={classes.circle}
        style={{ backgroundColor: props.color }}
      />
      P{props.priority}
    </span>
  );
};

const renderDateCell = (column, model) => {
  return moment
    .utc(model[column.field])
    .tz(moment.tz.guess(true))
    .format("lll z");
};

const renderAnalysisCell = (column, model) => {
  if (!model[column.field]) return;
  let string = model[column.field].replace(/(<([^>]+)>)/gi, ""); // regex to identify html tags and replace
  if (string.includes("{")) return <pre>{string}</pre>;
  return string;
};

const renderAverageTime = (column, model) => {
  if (!model[column.field]) return;

  // value is returned in seconds
  // if less than 48 hours, show hours, minutes and seconds
  // if more than 48 hours, show days, hours and minutes

  const timeInSeconds = model[column.field];
  let standardizedTime = moment.unix(timeInSeconds).utc();

  if (timeInSeconds < 172800)
    return standardizedTime.format("H [hours,] m [minutes and] s [seconds]");

  return standardizedTime.format("D [days,] H [hours and] m [minutes]");
};

// Default columns for the finding page, in order of columns in the table
export const defaultColumnFields = [
  "status",
  "created",
  "shortId",
  "type",
  "priority",
  "name",
  "analysis",
  "jurisdiction",
  "id",
];

const FindingsView = (props) => {
  const classes = useStyles();

  const [resolution, setResolution] = useState(10);
  const [selectedFindingIds, setSelectedFindingIds] = useState([]);

  const [textFilter, setTextFilter] = useState("");
  const [tableTextFilter, setTableTextFilter] = useState("");
  const [findingLink, setFindingLink] = useState({});
  const [viewingQuery, setViewingQuery] = useState(null);
  const [modelToDelete, setModelToDelete] = useState(false);
  const [isApplyingFilters, setIsApplyingFilters] = useState(false);
  const [activeColumns, setActiveColumns] = useState([]);
  const [viewingQueries, setViewingQueries] = useState([]);
  const [isViewingFilters, setIsViewingFilters] = useState(false);
  const [isPresetDialogOpen, setIsPresetDialogOpen] = useState(false);
  const [queryControls, queryControlInterface] = useQueryControls("findings");
  const [resultControls, resultControlInterface] =
    useResultControls("findings");
  const [isResolutionNotesDialogOpen, setIsResolutionNotesDialogOpen] =
    useState(false);
  const [saveOpen, setSaveOpen] = useState(false);
  const [delta, setDelta] = useState();

  const distinctCountColumn = { title: "Count", flex: 1, field: "count" };

  // ATTENTION ATTENTION
  // remove EVERYTHING BELOW when API is updated to handle these transforms
  const averageTimeColumn = {
    title: "Average Time",
    flex: 1,
    field: "mAvgSecondsToStatus",
    renderValue: renderAverageTime,
  };
  // remove EVERYTHING ABOVE when API is updated to handle these transforms
  // ATTENTION ATTENTION

  const lookups = props.lookups;
  const renderAssignees = useCallback(
    (column, model) => {
      const assignees = model.owners;
      if (!assignees) {
        return "";
      }
      const allAssignees = [];
      const seen = {};
      ["analysts", "responders", "managers"].forEach((type) => {
        if (assignees[type]) {
          assignees[type].forEach((userId) => {
            if (!seen[userId]) {
              seen[userId] = true;
              allAssignees.push(lookups.owners[userId] || userId);
            }
          });
        }
      });
      if (allAssignees.length > 0) return allAssignees.join(", ");
      return <span className={classes.noAssignees}>None</span>;
    },
    [lookups, classes]
  );

  const availableColumns = useMemo(
    () => [
      {
        title: "Status",
        flex: 1,
        field: "status",
        searchable: true,
        lookup: lookups.status,
      },
      {
        title: "Created",
        flex: 1,
        field: "created",
        renderValue: renderDateCell,
      },
      { title: "Short Id", flex: 1, field: "shortId", searchable: true },
      { title: "Type", flex: 1, field: "type", lookup: lookups.type },
      {
        title: "Priority",
        flex: 0.5,
        field: "priority",
        lookup: lookups.priority,
      },
      { title: "Name", flex: 1, field: "name", searchable: true },
      {
        title: "Analysis",
        flex: 1,
        field: "analysis",
        searchable: true,
        renderValue: renderAnalysisCell,
      },
      {
        title: "Jurisdiction",
        flex: 1,
        field: "jurisdiction",
        searchable: true,
        lookup: lookups.jurisdiction,
      },
      {
        title: "Category",
        flex: 1,
        field: "category",
        searchable: true,
        lookup: lookups.category,
      },
      {
        title: "Resolution",
        flex: 1,
        field: "resolution",
        searchable: true,
        lookup: lookups.resolution,
      },
      { title: "Summary", flex: 1, field: "summary", searchable: true },
      {
        title: "Blocked",
        flex: 0.5,
        field: "blocked",
        searchable: true,
        lookup: lookups.blocked,
      },
      {
        title: "Assignees",
        flex: 1,
        field: "owners",
        searchable: true,
        lookup: lookups.owners,
        renderValue: renderAssignees,
      },
    ],
    [lookups, renderAssignees]
  );
  const defaultColumns = useMemo(
    () =>
      defaultColumnFields
        .filter((fld) => fld !== "id")
        .map((fld) => availableColumns.find((col) => col.field === fld)),
    [availableColumns]
  );

  useEffect(() => {
    // if it exists get the deviceName query param
    // to set in state for filtering on page render
    const newTextFilter = get(props.query, ["deviceName"], null);

    if (newTextFilter) setTableTextFilter(newTextFilter);
  }, [props.query]);

  useEffect(() => {
    // if user is viewing a query honor
    // the active columns of said query
    if (!isApplyingFilters && !viewingQuery) setActiveColumns(defaultColumns);
  }, [defaultColumns]);

  useEffect(() => {
    setViewingQueries(props.queries);
  }, [props.queries]);

  useEffect(() => {
    if (props.ready) {
      const options = props.users.map((user) => ({
        value: user.id,
        label: `${user.firstName} ${user.lastName}`,
      }));
      findingColumnOptions.find((c) => c.field === "owners").options = options;
    }
  }, [props.ready, props.users]);

  useEffect(() => {
    setViewingQuery(null);
  }, [props.orgId]);

  useEffect(() => {
    if (Object.keys(queryControls).length > 0) {
      if (props.query) {
        // deviceName is a query param reserved for filtering the simple table
        // on page load when viewing from the Blu Agent Devices page
        Object.keys(props.query)
          .filter((key) => key !== "deviceName")
          .forEach((key) => {
            // check if the query is already present in queryControls since
            // we reload the page and query params could be added infinitely
            const alreadyAdded = Object.keys(queryControls).find(
              (qcUUID) => queryControls[qcUUID].field === key
            );

            // if the query param has not been added, we can add here
            // prior to reloading the page
            if (!alreadyAdded) {
              const qp = {
                field: key,
                operator: "eq",
                value: getValue("", props.query[key]),
              };

              queryControlInterface.add(qp);
            }
          });
      }
      props.reload();
    }
  }, [queryControls]);

  const handleApplyFilters = () => {
    const { reload } = props;

    const columnFields = activeColumns.map((col) => col.field);
    const isAverage = columnFields.find(
      (field) => field === "mAvgSecondsToStatus"
    );
    const isDistinct =
      resultControls.transform && resultControls.transform.key === "distinct";
    if (!isDistinct && !isAverage) columnFields.push("id");
    resultControlInterface.update("outputFields", columnFields);
    setIsApplyingFilters(true);
    reload(true);
  };

  const resetSearchPresets = () => {
    handleReset();
  };

  const clearAdvancedFilters = () => {
    handleReset();
    toggleViewingFilters();
  };

  const handleReset = (overrideControls = false) => {
    const { reload } = props;
    queryControlInterface.restore();
    resultControlInterface.restore();

    // use set times func to update
    // ui times and query controls
    let startTime = moment().add(-30, "days");
    let endTime = moment();
    handleSetTimes(startTime, endTime);

    setViewingQuery(null);
    setIsApplyingFilters(false);
    reload(true);
  };

  const handleSetTimes = (startTime, endTime) => {
    queryControlInterface.update(
      "startTime",
      moment.utc(startTime).startOf("minute").format("YYYY-MM-DDTHH:mm")
    );
    queryControlInterface.update(
      "endTime",
      moment.utc(endTime).endOf("minute").format("YYYY-MM-DDTHH:mm:ss.SSS999")
    );
    props.reload();
  };

  const handleSavedSelect = (e) => {
    const query = props.queries.find((q) => q.id === e.target.value);
    setViewingQuery(query);
    queryControlInterface.restore();
    resultControlInterface.restore();
    query.queryParams.forEach((qp) => {
      if (qp.field === "created") {
        if (
          (qp.operator === "gt" && qp.negate === false) ||
          (qp.operator === "lt" && qp.negate === true)
        ) {
          let newStartTime = moment.utc(qp.value);
          queryControlInterface.update(
            "startTime",
            newStartTime.format("YYYY-MM-DDTHH:mm")
          );
        } else if (
          (qp.operator === "lt" && qp.negate === false) ||
          (qp.operator === "gt" && qp.negate === true)
        ) {
          let newEndTime = moment.utc(qp.value).endOf("minute");
          queryControlInterface.update(
            "endTime",
            newEndTime.format("YYYY-MM-DDTHH:mm:ss.SSS999")
          );
        } else if (qp.operator === "in") {
          const t = new Time(qp.value[0]);
          const ranges = t.getTimeRange();
          queryControlInterface.update(
            "startTime",
            ranges.startTime.format("YYYY-MM-DDTHH:mm")
          );
          queryControlInterface.update(
            "endTime",
            ranges.endTime.format("YYYY-MM-DDTHH:mm:ss.SSS999")
          );
        }
      } else {
        queryControlInterface.add(qp);
      }
    });
    Object.keys(query.resultParams).map((k) =>
      resultControlInterface.update(k, query.resultParams[k])
    );
    if (query?.resultParams?.outputFields.length) {
      // use provided output fields if present
      // and update simple table columns
      let newActiveColumns = query.resultParams.outputFields
        .filter((fld) => fld !== "id")
        .map((fld) => availableColumns.find((col) => col.field === fld))
        .filter((col) => col);

      // ATTENTION ATTENTION
      // remove EVERYTHING BELOW when API is updated to handle these transforms
      // if average time is included add to active cols
      const isAverage = query.resultParams.outputFields.find(
        (fieldName) => fieldName === averageTimeColumn.field
      );
      newActiveColumns = isAverage
        ? [...newActiveColumns, averageTimeColumn]
        : newActiveColumns;

      if (!isAverage)
        resultControlInterface.update("outputFields", [
          ...query.resultParams.outputFields,
          ...["id"],
        ]);
      // remove EVERYTHING ABOVE when API is updated to handle these transforms
      // ATTENTION ATTENTION

      setActiveColumns(newActiveColumns);
    } else {
      // if no output fields provided, use default
      setActiveColumns(defaultColumns);
      resultControlInterface.update("outputFields", defaultColumnFields);
    }
    props.reload();
  };

  const handleTextFilterChange = (event) => {
    setTextFilter(event.target.value);

    if (event.target.value)
      setViewingQueries(
        props.queries.filter((q) =>
          q.name.toLowerCase().includes(event.target.value.toLowerCase())
        )
      );
    else setViewingQueries(props.queries);
  };

  // Context Menu Fucntions
  const handleMenuCopy = (contextMenu) => {
    copyTextToClipboard(contextMenu.anchorEl.innerText);
  };

  const handleViewDetails = (contextMenu) => {
    const { orgId } = props;
    const { id } = contextMenu.model;
    props.gotoFinding({ id, orgId });
  };

  const handleOpenInNewTab = (contextMenu) => {
    const { orgId } = props;
    const { model = {} } = contextMenu;
    setFindingLink({
      type: "PAGE",
      payload: {
        orgId: orgId,
        toplevel: "reporting",
        secondlevel: "findings",
        id1: model.id,
      },
    });
  };

  const onSave = (queryID) => {
    setSaveOpen(false);
    // ensure UI is updated with
    // up to date saved queries
    props.reload(true);
  };

  const toggleViewingFilters = () => {
    setIsViewingFilters(!isViewingFilters);
  };

  const exportData = (exportType) => {
    // Export raw cached data from RequestAction
    const exportData = props.exportData("findings", exportType);

    var mimeType = "text/plain";
    if (exportType === "csv") {
      mimeType = "text/csv";
    } else if (exportType === "jsonl") {
      mimeType = "application/ld+json";
    } else if (exportType === "json") {
      mimeType = "application/json";
    }

    const csvContent = `data:${mimeType};charset=UTF-8,${exportData}`;
    const encodedUri = encodeURI(csvContent);
    const downloadLink = document.createElement("a");
    downloadLink.setAttribute("href", encodedUri);
    downloadLink.setAttribute("download", `export.${exportType}`);
    document.body.appendChild(downloadLink);

    downloadLink.click();
  };

  const togglePresetDialog = () => {
    setModelToDelete(false);
    setIsPresetDialogOpen(!isPresetDialogOpen);
  };

  const toggleResolutionNotesDialog = () => {
    setIsResolutionNotesDialogOpen(!isResolutionNotesDialogOpen);
  };

  const handleSelectQuery = (id) => {
    handleSavedSelect({ target: { value: id } });
    togglePresetDialog();
  };

  const handleViewScheduledReports = () => {
    props.dispatch({
      type: "PAGE",
      payload: {
        orgId: props.orgId,
        toplevel: "reporting",
        secondlevel: "scheduledreports",
      },
    });
  };

  const setColumns = (columns) => {
    setActiveColumns(columns);
    resultControlInterface.update(
      "outputFields",
      columns.map((col) => col.field)
    );
  };

  const handleAddColumn = (event) => {
    if (
      event.target.value &&
      activeColumns.length !== availableColumns.length
    ) {
      const newColumns = event.target.value;
      setColumns(newColumns);
    }
  };

  const handleRemoveColumn = (column) => {
    const newColumns = activeColumns.filter((c) => c !== column);
    setColumns(newColumns);
  };

  const handleDistinctChange = (e) => {
    if (e.target.checked) {
      resultControlInterface.update("transform", [{ operator: "count" }]);
      setColumns(activeColumns);
    } else {
      resultControlInterface.remove("transform");
      setColumns(activeColumns);
    }
  };

  const isGlobal = (query) => !query?.orgId;

  const toggleConfirmDelete = (e, model) => {
    e.stopPropagation();
    setModelToDelete(model);
  };

  const handleCancel = (e) => {
    e.stopPropagation();
    setModelToDelete(false);
  };

  const handleDeleteQuery = () => {
    if (!modelToDelete) return;
    modelToDelete.delete().then(({ statusCode, data = {} }) => {
      if (statusCode === 200) {
        // if we  successfully delete we should
        // update the UI to display current viewing
        // queries without closing the modal
        let newViewingQueries = [...viewingQueries];
        setViewingQueries(newViewingQueries.filter((q) => q.id !== data.id));
        setViewingQuery(null); // UI should show 'all findings' as current selected preset
      }
    });
  };

  const handleMultiSelection = (option = {}, findingIds = []) => {
    const { value } = option;
    // selected value from const options
    setResolution(value);

    // set array in state for resolution
    setSelectedFindingIds(findingIds);

    // open dialog for optional resolution notes
    toggleResolutionNotesDialog();
  };

  const handleResolveFindings = (resolutionNotes) => {
    // build data with user notes
    const data = {
      status: 40,
      resolution: resolution,
      resolutionNotes: resolutionNotes,
    };

    // resolve every selected finding
    Promise.all(
      selectedFindingIds.map(async (findingId) => {
        await props.dispatchResolveFinding({
          data,
          findingId,
        });
      })
    ).then(() => {
      // close dialog
      toggleResolutionNotesDialog();

      // reload with resolved findings
      props.reload(true);
    });
  };

  const isDistinctChecked = () => {
    return (
      resultControls.transform &&
      resultControls.transform.find((c) => c.operator === "count")
    );
  };

  const filterActions = [
    {
      label: "Done",
      onClick: (editorValues) => {
        if (editorValues.uuid) {
          queryControlInterface.update(editorValues.uuid, editorValues);
        } else {
          queryControlInterface.add(editorValues);
        }
      },
    },
    {
      label: "Cancel",
      onClick: (editorValues) => {
        // noop.  Just closes editor
      },
    },
  ];

  const renderFilters = () => {
    if (isViewingFilters) {
      return (
        <div className={classes.filterContainer}>
          <div className={classes.filterSectionOuterContainer}>
            <div className={classes.advancedSectionContainer}>
              <FormControl>
                <InputLabel id="select-label">Show columns</InputLabel>
                <Select
                  multiple
                  value={activeColumns}
                  onChange={handleAddColumn}
                  labelId={"select-label"}
                  renderValue={() => (
                    <div>
                      {activeColumns.map((column) => (
                        <Chip
                          clickable
                          color={"primary"}
                          key={column.title}
                          label={column.title}
                          className={classes.chip}
                          onMouseDown={(event) => {
                            event.stopPropagation();
                          }}
                          onDelete={() => handleRemoveColumn(column)}
                        />
                      ))}
                    </div>
                  )}
                >
                  {availableColumns.filter(
                    (c) => !activeColumns.find((ac) => ac.title === c.title)
                  ).length ? (
                    availableColumns
                      .filter(
                        (c) => !activeColumns.find((ac) => ac.title === c.title)
                      )
                      .map((column) => (
                        <MenuItem key={column.title} value={column}>
                          {column.title}
                        </MenuItem>
                      ))
                  ) : (
                    <p value={null} className={classes.noColumnText}>
                      no columns available
                    </p>
                  )}
                </Select>
              </FormControl>
            </div>
            <div className={classes.advancedSectionContainer}>
              <p className={classes.titleText}>Filters</p>
              <FilterEditor
                controls={queryControls}
                interface={queryControlInterface}
                fields={findingColumnOptions}
                namespace="/finding"
                actions={filterActions}
              />
            </div>
            <div className={classes.distinctCountContainer}>
              <Switch
                color={"primary"}
                onChange={handleDistinctChange}
                checked={isDistinctChecked()}
              />
              <p className={classes.sectionText}>Apply Distinct Count</p>
            </div>
          </div>
          <div className={classes.buttonContainer}>
            <Button
              color={"primary"}
              variant={"contained"}
              onClick={handleApplyFilters}
            >
              Apply
            </Button>
            <Button onClick={() => clearAdvancedFilters()} color={"primary"}>
              Clear
            </Button>
          </div>
        </div>
      );
    }
    return null;
  };

  const renderFindingsData = () => {
    const doesContainSamples = props.findings.find(
      (finding) => finding.name && finding.name.includes("[SAMPLE]")
    );
    // If the org is a FREE edition org and doesn't have a cloud connector setup yet, then return the full list of findings
    // This will allow FREE orgs that have generated smaple findings to view those up until they setup a cloud connector
    if (
      props.license?.isTagged("limited-to-free") &&
      !props.integrations.length
    ) {
      return props.findings;
      // Otherwise, check to see if the findings list has any SAMPLE findings.  If it does then  filter them out.
    } else if (doesContainSamples) {
      return props.findings.filter((f) => !f.name.includes("[SAMPLE]"));
      // In all other cases, just return the findings list as it is
    } else {
      return props.findings;
    }
  };

  return props.findingId ? (
    <FindingsDetailView
      matches={props.matches}
      finding={props.finding ? props.finding[0] : null}
      findingId={props.findingId}
      orgId={props.orgId}
      users={props.users}
      ready={props.ready}
    />
  ) : (
    <div>
      <SaveRequestModal open={saveOpen} refName="findings" onClose={onSave} />
      <SimpleTable
        isNorthStar
        data={renderFindingsData()}
        searchData={tableTextFilter}
        initialOrderBy={["created", "desc"]}
        columns={[
          ...(resultControls.transform &&
          resultControls.transform.find((t) => t.operator === "count")
            ? [distinctCountColumn]
            : []),
          ...activeColumns,
        ]}
        exportData={exportData}
        isFetching={!props.ready}
        contextMenuItems={
          isDistinctChecked()
            ? [{ onClick: handleMenuCopy, text: "Copy to Clipboard" }]
            : [
                {
                  onClick: handleViewDetails,
                  text: "View Finding Details",
                  datacy: "findingsContextMenuItemFindingsDetail",
                },
                { onClick: handleMenuCopy, text: "Copy to Clipboard" },
                {
                  onClick: handleOpenInNewTab,
                  link: findingLink,
                  text: "Open in New Tab",
                },
              ]
        }
        moreMenuItems={[
          {
            label: "Reset Search",
            onClick: (event) => {
              resetSearchPresets();
            },
          },
          {
            label: "Save Preset",
            onClick: (event) => {
              setSaveOpen(true);
            },
          },
          {
            label: "Scheduled Reports",
            onClick: (event) => {
              handleViewScheduledReports();
            },
            licenseRestriction: "report.create",
            datacy: "scheduledReportsPageBtn",
          },
        ]}
        toolbarActions={[
          {
            isPrimary: true,
            component: (
              <div className={classes.inputContainer}>
                <p className={classes.labelText}>Search presets</p>
                <div
                  className={classes.textContainer}
                  onClick={togglePresetDialog}
                >
                  {viewingQuery ? (
                    <p>{viewingQuery.name}</p>
                  ) : (
                    <p style={{ fontWeight: 500 }}>All findings</p>
                  )}
                </div>
              </div>
            ),
          },
          {
            isPrimary: true,
            component: (
              <TimeControls
                controls={queryControls}
                delta={delta}
                setDelta={setDelta}
                interface={queryControlInterface}
                style={{ marginBottom: -22 }}
              />
            ),
          },
          {
            tooltip: "Advanced",
            onClick: (event) => {
              toggleViewingFilters();
            },
          },
        ]}
        filterComponent={renderFilters()}
        handleMultiSelection={handleMultiSelection}
        multiselectOptions={isDistinctChecked() ? false : resolutionOptions}
      />
      <Dialog
        onClose={togglePresetDialog}
        open={isPresetDialogOpen}
        maxWidth={"md"}
      >
        <div className={classes.presetDialog}>
          <div className={classes.titleOuterContainer}>
            <div className={classes.titleContainer}>
              <TextField
                value={textFilter}
                label={"Search Presets"}
                onChange={handleTextFilterChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                  disableUnderline: true,
                }}
              />
            </div>
            <IconButton onClick={togglePresetDialog}>
              <CloseIcon />
            </IconButton>
          </div>
          <div className={classes.queryContainer}>
            {viewingQueries &&
              viewingQueries.map((q, i) => (
                <div
                  key={i}
                  className={classes.queryNameContainer}
                  onClick={() => handleSelectQuery(q.id)}
                >
                  <p>{q.name}</p>
                  {/* 
                  if we have a global query show the global chip indicator
                  otherwise show a delete icon that allows the user to set a query 
                  model to be deleted if they confirm after pressing the delete icon
                */}
                  {isGlobal(q) ? (
                    <Chip icon={<PublicIcon />} label={"Global"} /> // OR
                  ) : modelToDelete && modelToDelete.id === q.id ? (
                    <Fade in={modelToDelete.id === q.id} timeout={500}>
                      <div className={classes.confirmContainer}>
                        <p>are you sure?</p>
                        <Button
                          variant={"text"}
                          className={classes.confirmDelete}
                          onClick={handleDeleteQuery}
                        >
                          delete preset
                        </Button>
                        <Button
                          variant={"text"}
                          color={"primary"}
                          onClick={handleCancel}
                        >
                          cancel
                        </Button>
                      </div>
                    </Fade> // OR
                  ) : (
                    <DeleteIcon
                      color={"primary"}
                      onClick={(e) => toggleConfirmDelete(e, q)}
                    />
                  )}
                </div>
              ))}
          </div>
        </div>
      </Dialog>
      <FindingsResolutionDialog
        open={isResolutionNotesDialogOpen}
        onClose={toggleResolutionNotesDialog}
        handleResolveFindings={(notes) => handleResolveFindings(notes)}
      />
    </div>
  );
};

const mapDispatchToProps = (dispatch) => {
  return {
    reload: (force) => dispatch(loadPageData(force)),
    exportData: (alias, exportType) =>
      dispatch(exportRequestAction(alias, exportType)),
    gotoFinding: ({ id, orgId }) => {
      dispatch({
        type: "PAGE",
        payload: {
          orgId,
          toplevel: "reporting",
          secondlevel: "findings",
          id1: id,
        },
      });
    },
    dispatchResolveFinding: ({ findingId, data }) => {
      dispatch(resolveFinding({ findingId, data }));
    },
  };
};

const mapStateToProps = (state, ownProps) => {
  const { location = {} } = state;
  const { query = {} } = location;

  const lookups = {
    jurisdiction: {
      10: "Administrator",
      20: "Manager",
      30: "Analyst",
      40: "Responder",
    },
    status: {
      0: "Open",
      10: "Analysis in Progress",
      20: "Analysis Complete",
      30: "Response in Progress",
      40: "Resolved",
    },
    type: {
      10: "Operational",
      15: "Risk",
      20: "Suspect",
      30: "Threat",
      40: "System",
    },
    category: {
      0: "Unknown",
      10: "Unauthorized Access Attempt",
      20: "Malicious Code",
      30: "Vulnerability Exploit Attempt",
      40: "Reconnaissance",
      50: "Denial of Service",
      60: "Policy Violation",
      70: "Assets",
      80: "Authentication",
      90: "Code Injection",
      100: "Collection",
      110: "Command and Control",
      120: "Command Line Logging",
      130: "Defense Evasion",
      140: "Discovery/Reconnaissance",
      150: "EventIDs",
      160: "Execution",
      170: "Exfiltration",
      180: "Initial Access",
      190: "Internal",
      200: "Lateral Movement",
      210: "Persistance",
      220: "Privilege Escalation",
      230: "Threats",
      240: "Users and Groups",
    },
    resolution: {
      10: "Valid",
      20: "False Positive",
      30: "No Action Needed",
      40: "Risk Accepted",
    },
    priority: {
      1: <Priority color="red" priority="1" />,
      2: <Priority color="#FDE03A" priority="2" />,
      3: <Priority color="gray" priority="3" />,
    },
    blocked: {
      false: "Not Blocked",
      true: "Blocked",
    },
  };

  if (ownProps.constants && ownProps.constants.length > 0) {
    Object.keys(lookups).forEach((l) => {
      if (ownProps.constants[0][l]) {
        ownProps.constants[0][l].forEach((s) => {
          lookups[l][s.id] = s.name;
        });
      }
    });
  }

  if (ownProps.users && ownProps.users.length > 0) {
    lookups.owners = {};
    ownProps.users.forEach((user) => {
      lookups.owners[user.id] = `${user.firstName} ${user.lastName}`;
    });
  }

  return {
    query,
    lookups,
    orgId: state.page.payload.orgId,
    findingId: state.location.payload.id1,
    license: state.license,
  };
};

FindingsView.defaultProps = {
  ready: false,
  users: [],
  queries: [],
  constants: [],
  findings: [],
  findingId: null,
  lookups: {
    jurisdiction: {},
    status: {},
    type: {},
    priority: {},
    blocked: {},
    owners: {},
  },
  integrations: [],
};

export default connect(mapStateToProps, mapDispatchToProps)(FindingsView);
